import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CreditBadge from "../components/CreditBadge";
import CreditAlert from "../components/CreditAlert";
import GalaxyBackground from "../components/GalaxyBackground";
import DeployModal from "../components/DeployModal";
import { toast } from "sonner";
import {
  Lightning, Plus, Trash, SignOut, FolderOpen, ArrowRight, CircleNotch, Sparkle, Crown, Rocket, Globe, CheckCircle,
} from "@phosphor-icons/react";

const IDEAS = [
  "A pomodoro timer with a circular progress ring",
  "A markdown note-taking app with live preview",
  "A landing page for a SaaS analytics product",
  "A snake game playable with arrow keys",
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [creating, setCreating] = useState(false);
  const [deployId, setDeployId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const res = await api.get("/api/projects");
      setProjects(res.data);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createProject = async (initialPrompt) => {
    const text = (initialPrompt ?? prompt).trim();
    if (!text) return;
    setCreating(true);
    try {
      const name = text.length > 40 ? text.slice(0, 40) + "…" : text;
      const res = await api.post("/api/projects", { name, description: text });
      navigate(`/workspace/${res.data.id}`, { state: { initialPrompt: text } });
    } catch (e) {
      toast.error("Could not create project");
      setCreating(false);
    }
  };

  const deleteProject = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/api/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const visibleProjects = filter === "published" ? projects.filter((p) => p.published) : projects;

  return (
    <div className="min-h-screen bg-forge-bg text-white relative noise-overlay">
      <div className="fixed inset-0 z-0 opacity-45 pointer-events-none">
        <GalaxyBackground density={0.7} />
      </div>
      <div className="fixed inset-0 z-0 pointer-events-none bg-gradient-to-b from-forge-bg/40 via-forge-bg/70 to-forge-bg" />
      <header className="relative z-10 border-b border-forge-border bg-forge-bg">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning size={24} weight="fill" className="text-forge-primary" />
            <span className="font-mono text-lg tracking-tight">Matrix</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-400 hidden sm:block font-mono">{user?.email}</span>
            {user?.is_admin && (
              <button
                data-testid="owner-console-btn"
                onClick={() => navigate("/admin")}
                className="flex items-center gap-1.5 text-sm text-forge-primary border border-forge-primary/40 px-3 py-1.5 rounded-sm hover:bg-forge-primary/10 transition-colors"
              >
                <Crown size={15} weight="fill" /> Owner
              </button>
            )}
            <CreditBadge />
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-1.5 text-sm text-neutral-300 hover:text-white border border-forge-border px-3 py-1.5 rounded-sm hover:bg-forge-hover transition-colors"
            >
              <SignOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <CreditAlert />

      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 py-12">
        {/* Create hero */}
        <div className="mb-14">
          <p className="uppercase text-xs tracking-[0.2em] text-forge-primary mb-3">New build</p>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-6">
            What do you want to build?
          </h1>
          <div className="border border-forge-border bg-forge-surface rounded-sm p-3 focus-within:ring-1 focus-within:ring-forge-primary focus-within:border-forge-primary transition-colors max-w-3xl">
            <textarea
              data-testid="new-project-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) createProject();
              }}
              rows={3}
              placeholder="Describe an app... e.g. a kanban board with drag and drop"
              className="w-full bg-transparent resize-none text-sm text-white placeholder:text-neutral-500 focus:outline-none px-1"
            />
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-neutral-500 font-mono">⌘/Ctrl + Enter to build</span>
              <button
                data-testid="create-project-btn"
                onClick={() => createProject()}
                disabled={creating || !prompt.trim()}
                className="flex items-center gap-2 bg-forge-primary text-black font-medium px-4 py-2 rounded-sm hover:bg-yellow-400 transition-colors disabled:opacity-50"
              >
                {creating ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
                Build with AI
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-3xl">
            {IDEAS.map((idea) => (
              <button
                key={idea}
                onClick={() => createProject(idea)}
                className="text-xs text-neutral-400 border border-forge-border px-3 py-1.5 rounded-sm hover:border-forge-primary hover:text-white transition-colors"
              >
                {idea}
              </button>
            ))}
          </div>
        </div>

        {/* Projects grid */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <FolderOpen size={20} className="text-forge-primary" />
            <h2 className="text-lg font-medium tracking-tight">Your projects</h2>
            <span className="text-sm text-neutral-500 font-mono">{projects.length}</span>
          </div>
          <div className="flex items-center border border-forge-border rounded-sm overflow-hidden">
            <button
              data-testid="filter-all"
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors ${filter === "all" ? "bg-forge-primary text-black" : "text-neutral-400 hover:text-white"}`}
            >
              All
            </button>
            <button
              data-testid="filter-published"
              onClick={() => setFilter("published")}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors flex items-center gap-1 ${filter === "published" ? "bg-forge-primary text-black" : "text-neutral-400 hover:text-white"}`}
            >
              <Globe size={12} /> Published {projects.filter((p) => p.published).length > 0 && `(${projects.filter((p) => p.published).length})`}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm">
            <CircleNotch size={18} className="animate-spin text-forge-primary" /> Loading...
          </div>
        ) : visibleProjects.length === 0 ? (
          <div className="border border-dashed border-forge-border rounded-sm p-12 text-center text-neutral-500">
            {filter === "published" ? "No published apps yet. Open a project and hit Deploy." : "No projects yet. Describe an app above to get started."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {visibleProjects.map((p) => (
              <div
                key={p.id}
                data-testid={`project-card-${p.id}`}
                onClick={() => navigate(`/workspace/${p.id}`)}
                className="group relative border border-forge-border bg-forge-surface rounded-sm p-5 cursor-pointer hover:border-forge-primary hover:bg-forge-hover transition-colors flex flex-col"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-forge-primary/10 border border-forge-primary/30 rounded-sm">
                    <Lightning size={18} weight="fill" className="text-forge-primary" />
                  </div>
                  <button
                    data-testid={`delete-project-${p.id}`}
                    onClick={(e) => deleteProject(p.id, e)}
                    className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-400 transition-opacity"
                  >
                    <Trash size={16} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-medium text-white truncate">{p.name}</h3>
                  {p.published && (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-green-400 border border-green-500/40 bg-green-500/10 px-1.5 py-0.5 rounded-sm" data-testid={`live-badge-${p.id}`}>
                      <CheckCircle size={9} weight="fill" /> Live
                    </span>
                  )}
                </div>
                <p className="text-xs text-neutral-500 font-mono mb-4">{p.files?.length || 0} files</p>

                <div className="mt-auto flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1 text-xs text-forge-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Open <ArrowRight size={12} weight="bold" />
                  </span>
                  <button
                    data-testid={`card-deploy-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); setDeployId(p.id); }}
                    disabled={!p.files?.length}
                    title={p.files?.length ? "Deploy this app" : "Generate the app first"}
                    className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-sm border transition-colors disabled:opacity-40 ${p.published ? "border-green-500/40 text-green-400 hover:bg-green-500/10" : "border-forge-primary/50 text-forge-primary hover:bg-forge-primary hover:text-black"}`}
                  >
                    <Rocket size={12} weight="fill" /> {p.published ? "Manage" : "Deploy"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <DeployModal
        projectId={deployId}
        open={!!deployId}
        onOpenChange={(o) => { if (!o) setDeployId(null); }}
        onDeployed={load}
      />
    </div>
  );
}
